import { useState, useEffect, useMemo, useCallback } from 'react';
import { approvalsService } from '../services/approvalsService';
import { Timecard } from '../types/Timecard';
import { transformTimecardsToApprovalData } from '../utils/approvalDataUtils';
import { ForemanAreaApprovalData } from '../types/ApprovalData';

export interface ApprovalDataFilter {
  foremanAreaId?: number;
  serviceAreaId?: number;
}

interface UseApprovalDataReturn {
  data: ForemanAreaApprovalData[];
  loading: boolean;
  error: Error | null;
  refresh: () => void;
  lastUpdated: Date | null;
}

/**
 * Custom hook to fetch and transform approval timecard data.
 * Fetches timecards from the approvals service and transforms them into
 * ForemanAreaApprovalData format grouped by foreman area.
 * When filter is provided (e.g. from home page global filter), only data for that area is fetched.
 *
 * @param filter - Optional { foremanAreaId, serviceAreaId } to scope the request (server-side filter).
 * @returns Object containing data, loading state, error, refresh function, and lastUpdated timestamp
 */
export const useApprovalData = (filter?: ApprovalDataFilter | null): UseApprovalDataReturn => {
  const [timecards, setTimecards] = useState<Timecard[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Transform timecards into approval data format
  const data = useMemo(() => {
    return transformTimecardsToApprovalData(timecards);
  }, [timecards]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const foremanAreaId = filter?.foremanAreaId;
      const serviceAreaId = filter?.serviceAreaId;
      const fetchedTimecards = await approvalsService.getTimeCards(foremanAreaId, serviceAreaId);
      setTimecards(fetchedTimecards);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch approval data'));
      setTimecards([]);
    } finally {
      setLoading(false);
    }
  }, [filter?.foremanAreaId, filter?.serviceAreaId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refresh,
    lastUpdated,
  };
};
